import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { WORK_ORDER_STATUS, useWorkOrdersStore } from "../../stores/work_orders";
import Search from "../form/Search";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "./Modal";

// Picker for linking a delivery note to a work order. The backend is
// authoritative for permissions; inactive work orders are disabled up front.
function LinkWorkOrderDialog({ onSelect, onClose }) {
  const { t } = useTranslation();
  const searchSome = useWorkOrdersStore((state) => state.searchSome);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    let active = true;
    searchSome({ q: search, limit: 10 }).then(({ data }) => {
      if (active) {
        setResults(data || []);
      }
    });
    return () => {
      active = false;
    };
  }, [search, searchSome]);

  return (
    <Modal size="lg">
      <ModalHeader>
        <h5 className="modal-title">{t("delivery_notes.link_work_order")}</h5>
      </ModalHeader>
      <ModalBody>
        <Search search={search} handleSearch={(e) => setSearch(e.target.value)} />
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th className="text-nowrap" style={{ width: "1%" }}>
                  {t("work_orders.work_order")}
                </th>
                <th>{t("customers.customer")}</th>
                <th>{t("customers.properties.property")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {results.map((workOrder) => {
                const isActive =
                  workOrder.status === WORK_ORDER_STATUS.OPEN ||
                  workOrder.status === WORK_ORDER_STATUS.IN_PROGRESS;
                const rowClass = isActive ? "" : "text-muted";
                const disabledTitle = isActive ? undefined : t("work_orders.errors.not_active");
                // Disabled buttons swallow hover events (no native tooltip in
                // Firefox); pe-none routes hover to the title-bearing wrapper.
                const linkButtonClass = disabledTitle
                  ? "btn btn-sm btn-success pe-none"
                  : "btn btn-sm btn-success";
                const customer = workOrder.customer;
                const customerProperty = workOrder.customer_property;
                const propertyName = customerProperty?.name?.trim();
                const customerName = customer?.name?.trim();
                const showCustomerName = !!customerName && customerName !== propertyName;
                return (
                  <tr key={workOrder.id} className={rowClass}>
                    <td className="text-nowrap">
                      <Link to={`/work_orders/${workOrder.id}/details`} className="fw-bold">
                        {workOrder.number}
                      </Link>
                      <div className="mt-1">
                        <span className="badge bg-default text-dark">
                          {t(`work_orders.states.${workOrder.status}`)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <ul className="list-group list-unstyled">
                        {!!customer?.number && <li>{customer.number}</li>}
                        {showCustomerName && (
                          <li className="text-truncate" style={{ maxWidth: "25ch" }} title={customerName}>
                            {customerName}
                          </li>
                        )}
                      </ul>
                    </td>
                    <td>
                      <ul className="list-group list-unstyled">
                        {!!propertyName && (
                          <li className="text-truncate" style={{ maxWidth: "25ch" }} title={propertyName}>
                            {propertyName}
                          </li>
                        )}
                        {customerProperty?.addresses?.map((address) => {
                          const nameMatchesStreet = propertyName === address.street?.trim();
                          return (
                            <li key={address.id}>
                              <ul className="list-group list-unstyled">
                                {!!address.street && !nameMatchesStreet && <li>{address.street}</li>}
                                {(!!address.zip || !!address.city) && (
                                  <li>
                                    {address.zip} {address.city}
                                  </li>
                                )}
                              </ul>
                            </li>
                          );
                        })}
                      </ul>
                    </td>
                    <td className="text-end">
                      <span className="d-inline-block" title={disabledTitle}>
                        <button
                          type="button"
                          className={linkButtonClass}
                          disabled={!isActive}
                          onClick={() => onSelect(workOrder.id)}
                        >
                          {t("work_orders.link")}
                        </button>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-default" onClick={onClose}>
          {t("menu.cancel")}
        </button>
      </ModalFooter>
    </Modal>
  );
}

LinkWorkOrderDialog.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default LinkWorkOrderDialog;
